import React, { useEffect, useMemo, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import MobileLayout from './components/mobile/MobileLayout';
import { DeviceProvider, ResponsivePage, useResponsivePortal } from './components/ResponsivePortal';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MobileDashboard from './pages/mobile/MobileDashboard';
import SendTest from './pages/SendTest';
import MobileSendTest from './pages/mobile/MobileSendTest';
import Results from './pages/Results';
import FlaggedFollowUps from './pages/FlaggedFollowUps';
import MobileFlaggedFollowUps from './pages/mobile/MobileFlaggedFollowUps';
import Clients from './pages/Clients';
import Earnings from './pages/Earnings';
import MobileEarnings from './pages/mobile/MobileEarnings';
import Profile from './pages/Profile';
import MobileProfile from './pages/mobile/MobileProfile';
import ChangePassword from './pages/ChangePassword';
import MobileChangePassword from './pages/mobile/MobileChangePassword';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <ResponsivePrivateLayout>{children}</ResponsivePrivateLayout>;
};

const ResponsivePrivateLayout = ({ children }) => {
  const { isMobilePhone } = useResponsivePortal();
  const PortalLayout = isMobilePhone ? MobileLayout : Layout;

  return <PortalLayout>{children}</PortalLayout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><ResponsivePage desktop={Dashboard} mobile={MobileDashboard} /></PrivateRoute>} />
      <Route path="/send-test" element={<PrivateRoute><ResponsivePage desktop={SendTest} mobile={MobileSendTest} /></PrivateRoute>} />
      <Route path="/results" element={<PrivateRoute><ResponsivePage desktop={Results} mobile={Results} /></PrivateRoute>} />
      <Route path="/flagged-follow-ups" element={<PrivateRoute><ResponsivePage desktop={FlaggedFollowUps} mobile={MobileFlaggedFollowUps} /></PrivateRoute>} />
      <Route path="/clients" element={<PrivateRoute><ResponsivePage desktop={Clients} mobile={Clients} /></PrivateRoute>} />
      <Route path="/earnings" element={<PrivateRoute><ResponsivePage desktop={Earnings} mobile={MobileEarnings} /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><ResponsivePage desktop={Profile} mobile={MobileProfile} /></PrivateRoute>} />
      <Route path="/change-password" element={<PrivateRoute><ResponsivePage desktop={ChangePassword} mobile={MobileChangePassword} /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  const isStandaloneMode = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const shouldShowInstallButton = !isStandaloneMode && !isInstalled && Boolean(deferredPrompt);

  return (
    <Router>
      <AuthProvider>
        <DeviceProvider>
          <AppRoutes />
        {shouldShowInstallButton && (
          <button
            type="button"
            onClick={handleInstallClick}
            style={{
              position: 'fixed',
              right: '16px',
              bottom: '16px',
              zIndex: 1000,
              border: 'none',
              borderRadius: '999px',
              padding: '12px 16px',
              background: '#4f46e5',
              color: '#ffffff',
              fontWeight: 700,
              boxShadow: '0 10px 24px rgba(79,70,229,0.35)',
              cursor: 'pointer'
            }}
          >
            Install App
          </button>
        )}
          <ToastContainer position="top-right" autoClose={3000} />
        </DeviceProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
